import { motion } from "framer-motion";
import { Textarea } from "@/components/ui/textarea";
import { Target, ShoppingBag, Sparkles, Settings, Lightbulb } from "lucide-react";

const intentOptions = [
  { 
    id: "promotional", 
    label: "Promotional", 
    icon: Target, 
    selectedClass: "bg-viraly-orange/20 border-viraly-orange ring-1 ring-viraly-orange/30",
    iconClass: "text-viraly-orange",
    dotClass: "bg-viraly-orange",
    placeholder: "e.g., Highlight the discount, create urgency, focus on the main offer..."
  },
  { 
    id: "showcase", 
    label: "Showcase", 
    icon: ShoppingBag, 
    selectedClass: "bg-blue-500/20 border-blue-500 ring-1 ring-blue-500/30",
    iconClass: "text-blue-500",
    dotClass: "bg-blue-500",
    placeholder: "e.g., Feature the product details, show the key benefits..."
  },
  { 
    id: "brand", 
    label: "Brand Awareness", 
    icon: Sparkles, 
    selectedClass: "bg-purple-500/20 border-purple-500 ring-1 ring-purple-500/30",
    iconClass: "text-purple-500",
    dotClass: "bg-purple-500",
    placeholder: "e.g., Emphasize company values, build trust, share the story..."
  },
  { 
    id: "custom", 
    label: "Custom", 
    icon: Settings, 
    selectedClass: "bg-emerald-500/20 border-emerald-500 ring-1 ring-emerald-500/30",
    iconClass: "text-emerald-500",
    dotClass: "bg-emerald-500",
    placeholder: "e.g., Create specific content for my Christmas campaign, focus on luxury features..."
  },
];

export const UrlIntentWithContext = ({ 
  intent, 
  onIntentChange, 
  context, 
  onContextChange,
  onShowListingDetails 
}) => {
  const selectedIntent = intentOptions.find(i => i.id === intent) || intentOptions[0];
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-5 rounded-xl bg-white/5 border border-white/10 space-y-4"
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        <Target className="w-5 h-5 text-viraly-orange" />
        <h4 className="font-medium text-viraly-light">What would you like to create?</h4>
      </div>
      
      {/* Intent Pills */}
      <div className="grid grid-cols-2 gap-2">
        {intentOptions.map((option) => {
          const Icon = option.icon;
          const isSelected = intent === option.id;
          
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => {
                onIntentChange(option.id);
                // Auto-show listing details for custom intent
                if (option.id === "custom" && onShowListingDetails) {
                  onShowListingDetails(true);
                }
              }}
              className={`p-3 rounded-lg text-left transition-all flex items-center gap-2 ${
                isSelected
                  ? option.selectedClass
                  : "bg-white/5 border-white/10 border hover:bg-white/10"
              }`}
              data-testid={`url-intent-${option.id}`}
            >
              <Icon className={`w-4 h-4 ${isSelected ? option.iconClass : 'text-viraly-muted'}`} />
              <span className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-viraly-light'}`}>
                {option.label}
              </span>
              {isSelected && (
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className={`ml-auto w-2 h-2 rounded-full ${option.dotClass}`}
                />
              )}
            </button>
          );
        })}
      </div>
      
      {/* Context Input - Always visible, contextual placeholder */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-viraly-orange" />
          <label className="text-sm font-medium text-viraly-light">
            Add your vision
          </label>
        </div>
        
        <Textarea
          value={context}
          onChange={(e) => onContextChange(e.target.value)}
          placeholder={selectedIntent.placeholder}
          className="min-h-[80px] bg-white/5 border-white/10 text-viraly-light placeholder:text-viraly-muted/60 focus:border-viraly-orange/50 focus:ring-viraly-orange/20 resize-none text-sm"
          data-testid="url-context-input"
        />
        
        {/* Character count - subtle */}
        <div className="flex items-center justify-between">
          <p className="text-[10px] text-viraly-muted/60">
            The more you share, the closer we get to perfection
          </p>
          {context.length > 0 && (
            <span className="text-[10px] text-viraly-muted/50">
              {context.length} chars
            </span>
          )}
        </div>
      </div>
      
      {/* Quick suggestion chips */}
      {!context && (
        <div className="flex flex-wrap gap-2 pt-1">
          {[
            "Focus on the main product",
            "Make it festive/seasonal",
            "Emphasize the discount",
            "Target luxury buyers"
          ].map((suggestion, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => onContextChange(suggestion)}
              className="text-[10px] px-2 py-1 rounded-full bg-white/5 text-viraly-muted hover:bg-viraly-orange/10 hover:text-viraly-orange transition-colors"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default UrlIntentWithContext;
